import React, { useCallback, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components/macro';
import { useLottie } from 'lottie-react';
import {
  bounce,
  bounceIn,
  bounceInDown,
  bounceInLeft,
  bounceInRight,
  bounceInUp,
  fadeIn,
  fadeOut,
  fadeInDown,
  fadeInLeft,
  fadeInRight,
  fadeInUp,
  flip,
  flipInX,
  flipInY,
  headShake,
  jello,
  lightSpeedIn,
  pulse,
  rollIn,
  rotateIn,
  rubberBand,
  shake,
  slideInDown,
  slideInLeft,
  slideInRight,
  slideInUp,
  swing,
  tada,
  wobble,
  zoomIn,
  zoomInDown,
  zoomInLeft,
  zoomInRight,
  zoomInUp,
} from 'react-animations';
import { Animation } from 'react-animations';
import { Box, useColorMode } from '@televet/kibble-ui/build/chakra';
import { Logo } from '@televet/kibble-ui/build/components/Logo';
import usePersistedState from 'shared/hooks/usePersistedState';
import { PersistedStateKeys } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';

const animations = [
  bounce,
  bounceIn,
  bounceInDown,
  bounceInLeft,
  bounceInRight,
  bounceInUp,
  fadeIn,
  fadeInDown,
  fadeInLeft,
  fadeInRight,
  fadeInUp,
  flip,
  flipInX,
  flipInY,
  headShake,
  jello,
  lightSpeedIn,
  pulse,
  rollIn,
  rotateIn,
  rubberBand,
  shake,
  slideInDown,
  slideInLeft,
  slideInRight,
  slideInUp,
  swing,
  tada,
  wobble,
  zoomIn,
  zoomInDown,
  zoomInLeft,
  zoomInRight,
  zoomInUp,
];

const getRandomAnimation = (animations: Animation[]): Animation => {
  const randomIndex = Math.floor(Math.random() * Object.keys(animations).length);
  return animations[randomIndex];
};

const LoadingAndPresentation = (): JSX.Element => {
  const { clinicUser } = useClinicUser();
  const { colorMode } = useColorMode();

  const [persistedIsIntroDisabled, setPersistedIsIntroDisabled] = usePersistedState(
    PersistedStateKeys.IsIntroDisabled,
    false,
  );
  const [presentationDisplay, setPresentationDisplay] = useState(!persistedIsIntroDisabled);

  useEffect(() => {
    setPersistedIsIntroDisabled(!!clinicUser?.userSetting?.skipLoadingAnimation);
  }, [clinicUser?.userSetting?.skipLoadingAnimation, setPersistedIsIntroDisabled]);

  const onkeyDown = (event: KeyboardEvent): void => {
    console.warn("Presentation skipped by user's keyboard input", event.key);
    removeEventListeners();
    setPresentationDisplay(false);
  };

  const onMouseDown = (): void => {
    console.warn("Presentation skipped by user's mouse input");
    removeEventListeners();
    setPresentationDisplay(false);
  };

  const addEventListeners = (): void => {
    document.addEventListener('keydown', onkeyDown);
    document.addEventListener('click', onMouseDown);
  };

  const removeEventListeners = (): void => {
    document.removeEventListener('keydown', onkeyDown);
    document.removeEventListener('click', onMouseDown);
  };

  useEffect(() => {
    addEventListeners();
    const presentationDisplay = setTimeout(() => {
      removeEventListeners();
      setPresentationDisplay(false);
    }, 2500);

    return (): void => {
      removeEventListeners();
      clearTimeout(presentationDisplay);
    };
    //eslint-disable-next-line
  }, []);

  const { View: Xmas } = useLottie({ animationData: require('./LogoAnimations/Xmas.json'), loop: true });

  const PresentationLogo = useCallback((): JSX.Element => {
    const date = new Date();
    const month = date.getMonth();
    const day = date.getDate();

    if (month === 11 && day >= 14 && day <= 31) {
      return Xmas;
    }

    return <Logo w="100%" color={colorMode === 'dark' ? 'primaryOnContrast' : 'primary'} />;
  }, [Xmas, colorMode]);

  return presentationDisplay ? (
    <LoadingAndPresentationBg>
      <LoadingAndPresentationContainer>
        <Box width="70%" maxWidth="320px">
          <LogoAnimationFadeIn>
            <LogoAnimationIn>{PresentationLogo()}</LogoAnimationIn>
          </LogoAnimationFadeIn>
        </Box>
      </LoadingAndPresentationContainer>
    </LoadingAndPresentationBg>
  ) : (
    <></>
  );
};

export default LoadingAndPresentation;

const animationFadeInKeyframe = keyframes`${fadeIn}`;

const animationKeyframe = keyframes`${getRandomAnimation(animations)}`;

const animationOutKeyframe = keyframes`${fadeOut}`;

const LoadingAndPresentationBg = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: var(--chakra-colors-background-default);
  z-index: 999999;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: 0.5s ${animationOutKeyframe};
  animation-delay: 2s;
`;

const LoadingAndPresentationContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: var(--chakra-colors-background-primaryAlpha);
  z-index: 9999999;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LogoAnimationFadeIn = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  animation: 0.5s ${animationFadeInKeyframe};
`;

const LogoAnimationIn = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  animation: 1.5s ${animationKeyframe};
`;
